// Period totals for the Mileage screen, ported from the app's MileageSummary.

import type { Category, CategoryCatalog } from "./categories.ts";
import type { Interval } from "./periods.ts";
import { dayCount } from "./periods.ts";
import { type MileageRates, type ResolvedRate, rateValue } from "./rates.ts";

export interface SummaryTrip {
  start: Date;
  meters: number;
  categoryRaw: string | null;
}

export interface CategoryTotal {
  category: Category;
  tripCount: number;
  meters: number;
  value: number | undefined;
  /** The rate in force at the end of the period (or now). */
  rate: ResolvedRate | undefined;
  /** Trips were valued at more than one rate, such as a mid-year change. */
  ratesVary: boolean;
  /** The lowest and highest rate used, for "$0.725 – $0.76/mi". */
  rateRange: [number, number] | undefined;
}

export interface Bucket {
  start: Date;
  metersByCategory: Map<string, number>;
  isPartial: boolean;
  isFuture: boolean;
}

export function bucketTotal(bucket: Bucket): number {
  let total = 0;
  for (const meters of bucket.metersByCategory.values()) total += meters;
  return total;
}

export const maxDailyDays = 31;

export class MileageSummary {
  readonly tripCount: number;
  readonly totalMeters: number;
  readonly deductibleMeters: number;
  readonly totalValue: number;
  readonly averageValuePerMonth: number;
  readonly categories: CategoryTotal[];
  readonly unclassifiedCount: number;
  readonly unclassifiedMeters: number;
  readonly months: Bucket[];
  readonly days: Bucket[];
  readonly interval: Interval;

  constructor(trips: readonly SummaryTrip[], catalog: CategoryCatalog, rates: MileageRates, interval: Interval, now: Date) {
    this.interval = interval;
    const inPeriod = trips.filter((trip) => interval.start <= trip.start && trip.start < interval.end);
    this.tripCount = inPeriod.length;
    this.totalMeters = inPeriod.reduce((sum, trip) => sum + trip.meters, 0);

    let deductibleMeters = 0;
    let totalValue = 0;
    let unclassifiedCount = 0;
    let unclassifiedMeters = 0;
    const byCategory = new Map<string, CategoryTotal>();
    const ratesUsed = new Map<string, Set<number>>();

    for (const trip of inPeriod) {
      const category = catalog.resolve(trip.categoryRaw);
      if (!category) {
        unclassifiedCount += 1;
        unclassifiedMeters += trip.meters;
        continue;
      }
      const total = byCategory.get(category.rawValue)
        ?? { category, tripCount: 0, meters: 0, value: undefined, rate: undefined, ratesVary: false, rateRange: undefined };
      total.tripCount += 1;
      total.meters += trip.meters;
      const rate = rates.rate(category.rawValue, trip.start);
      if (rate) {
        const value = rateValue(rate, trip.meters);
        const used = ratesUsed.get(category.rawValue) ?? new Set<number>();
        used.add(rate.amountTenThousandths);
        ratesUsed.set(category.rawValue, used);
        total.value = (total.value ?? 0) + value;
        totalValue += value;
        deductibleMeters += trip.meters;
      }
      byCategory.set(category.rawValue, total);
    }

    const rateDate = new Date(Math.min(now.getTime(), interval.end.getTime() - 1));
    this.categories = [...byCategory.values()]
      .sort((a, b) => a.category.listRank - b.category.listRank)
      .map((total) => {
        const used = [...(ratesUsed.get(total.category.rawValue) ?? [])].sort((a, b) => a - b);
        return {
          ...total,
          rate: rates.rate(total.category.rawValue, rateDate),
          ratesVary: used.length > 1,
          rateRange: used.length > 0 ? [used[0]! / 10_000, used.at(-1)! / 10_000] : undefined,
        };
      });

    this.deductibleMeters = deductibleMeters;
    this.totalValue = totalValue;
    this.unclassifiedCount = unclassifiedCount;
    this.unclassifiedMeters = unclassifiedMeters;
    this.months = buckets(inPeriod, catalog, interval, "month", now);
    this.days = dayCount(interval) <= maxDailyDays ? buckets(inPeriod, catalog, interval, "day", now) : [];
    const begunMonths = Math.max(1, this.months.filter((month) => !month.isFuture).length);
    this.averageValuePerMonth = totalValue / begunMonths;
  }

  get currencyCode(): string | undefined {
    const codes = new Set(this.categories.map((total) => total.rate?.currencyCode).filter((code): code is string => !!code));
    return codes.size === 1 ? [...codes][0] : undefined;
  }
}

function startOf(date: Date, component: "month" | "day"): Date {
  return component === "month"
    ? new Date(date.getFullYear(), date.getMonth(), 1)
    : new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function next(date: Date, component: "month" | "day"): Date {
  return component === "month"
    ? new Date(date.getFullYear(), date.getMonth() + 1, 1)
    : new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
}

function buckets(trips: readonly SummaryTrip[], catalog: CategoryCatalog, interval: Interval, component: "month" | "day", now: Date): Bucket[] {
  const result: Bucket[] = [];
  const current = startOf(now, component).getTime();
  let start = startOf(interval.start, component);
  while (start < interval.end) {
    const end = next(start, component);
    const metersByCategory = new Map<string, number>();
    for (const trip of trips) {
      if (trip.start < start || trip.start >= end) continue;
      const category = catalog.resolve(trip.categoryRaw);
      if (!category) continue;
      metersByCategory.set(category.rawValue, (metersByCategory.get(category.rawValue) ?? 0) + trip.meters);
    }
    result.push({ start, metersByCategory, isPartial: start.getTime() === current, isFuture: start > now });
    start = end;
  }
  return result;
}
